/**
 * SessionStorage 管理工具
 * 用于在页面跳转（如微信支付）前后保存和恢复 sessionStorage 数据
 * 
 * 特性：
 * - 自动备份所有 sessionStorage 数据（除黑名单外）
 * - 支持黑名单模式，灵活排除不需要备份的数据
 * - 支持正则表达式模式匹配，自动排除临时数据
 * - 30分钟自动过期机制，避免占用存储空间
 * - 完整的调试信息支持
 */

// 备份存储键名
const BACKUP_KEY = 'tianji_sessionStorage_backup'
const BACKUP_TIMESTAMP_KEY = 'tianji_backup_timestamp'

// 备份过期时间（30分钟）
const BACKUP_EXPIRY_TIME = 30 * 60 * 1000

// 不需要备份的 sessionStorage 键名列表（黑名单模式）
const EXCLUDE_KEYS = [
  // 临时或敏感数据，不需要备份
  'tianji_payment_restored', // 临时支付恢复标记
  // 可以在这里添加其他不需要备份的键名
]

// 检查键名是否应该被备份
const shouldBackupKey = (key: string): boolean => {
  // 排除黑名单中的键
  if (EXCLUDE_KEYS.includes(key)) {
    return false
  }
  
  // 排除一些常见的临时或系统键名模式
  const excludePatterns = [
    /^temp_/i,          // 临时数据 (忽略大小写)
    /^cache_/i,         // 缓存数据  
    /^debug_/i,         // 调试数据
    /^test_/i,          // 测试数据
    /_temp$/i,          // 以_temp结尾的数据
    /_cache$/i,         // 以_cache结尾的数据
  ]
  
  return !excludePatterns.some(pattern => pattern.test(key))
}

export interface SessionBackupData {
  [key: string]: string | null
}

/**
 * 备份 sessionStorage 数据到 localStorage
 */
export function backupSessionStorage(): void {
  try {
    const backupData: SessionBackupData = {}
    
    // 遍历所有 sessionStorage 键值对
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && shouldBackupKey(key)) {
        const value = sessionStorage.getItem(key)
        if (value !== null) {
          backupData[key] = value
        }
      }
    }
    
    // 保存备份数据和时间戳到 localStorage
    localStorage.setItem(BACKUP_KEY, JSON.stringify(backupData))
    localStorage.setItem(BACKUP_TIMESTAMP_KEY, Date.now().toString())
    
    console.log('SessionStorage 备份完成:', Object.keys(backupData))
  } catch (error) {
    console.error('备份 sessionStorage 失败:', error)
  }
}

/**
 * 从 localStorage 恢复 sessionStorage 数据
 */
export function restoreSessionStorage(): boolean {
  try {
    const backupDataStr = localStorage.getItem(BACKUP_KEY)
    const timestampStr = localStorage.getItem(BACKUP_TIMESTAMP_KEY)
    
    if (!backupDataStr || !timestampStr) {
      console.log('没有找到 sessionStorage 备份数据')
      return false
    }
    
    // 检查备份是否过期
    const timestamp = parseInt(timestampStr)
    const now = Date.now()
    if (now - timestamp > BACKUP_EXPIRY_TIME) {
      console.log('SessionStorage 备份已过期，清除备份数据')
      clearBackup()
      return false
    }
    
    const backupData: SessionBackupData = JSON.parse(backupDataStr)
    
    // 恢复数据到 sessionStorage
    Object.entries(backupData).forEach(([key, value]) => {
      if (value !== null) {
        sessionStorage.setItem(key, value)
      }
    })
    
    console.log('SessionStorage 恢复完成:', Object.keys(backupData))
    
    // 恢复完成后清除备份
    clearBackup()
    
    return true
  } catch (error) {
    console.error('恢复 sessionStorage 失败:', error)
    clearBackup()
    return false
  }
}

/**
 * 清除备份数据
 */
export function clearBackup(): void {
  localStorage.removeItem(BACKUP_KEY)
  localStorage.removeItem(BACKUP_TIMESTAMP_KEY)
}

/**
 * 检查是否有可用的备份数据
 */
export function hasValidBackup(): boolean {
  const backupDataStr = localStorage.getItem(BACKUP_KEY)
  const timestampStr = localStorage.getItem(BACKUP_TIMESTAMP_KEY)
  
  if (!backupDataStr || !timestampStr) {
    return false
  }
  
  const timestamp = parseInt(timestampStr)
  const now = Date.now()
  
  return (now - timestamp) <= BACKUP_EXPIRY_TIME
}

/**
 * 获取备份信息（用于调试）
 */
export function getBackupInfo(): { hasBackup: boolean; timestamp?: number; keys?: string[]; totalKeys?: number; excludedKeys?: string[] } {
  const backupDataStr = localStorage.getItem(BACKUP_KEY)
  const timestampStr = localStorage.getItem(BACKUP_TIMESTAMP_KEY)
  
  if (!backupDataStr || !timestampStr) {
    return { hasBackup: false }
  }
  
  try {
    const backupData: SessionBackupData = JSON.parse(backupDataStr)
    
    // 获取当前被排除的键名（用于调试）
    const excludedKeys: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && !shouldBackupKey(key)) {
        excludedKeys.push(key)
      }
    }
    
    return {
      hasBackup: true,
      timestamp: parseInt(timestampStr),
      keys: Object.keys(backupData),
      totalKeys: Object.keys(backupData).length,
      excludedKeys
    }
  } catch {
    return { hasBackup: false }
  }
}

/**
 * 支付跳转专用：备份当前数据并标记支付状态
 */
export function backupForPayment(redirectUrl: string): void {
  try {
    // 执行常规备份
    backupSessionStorage()
    
    // 额外保存支付相关信息
    const paymentInfo = {
      redirectUrl,
      timestamp: Date.now(),
      fromPayment: true
    }
    
    localStorage.setItem('tianji_payment_context', JSON.stringify(paymentInfo))
    console.log('支付跳转备份完成')
  } catch (error) {
    console.error('支付跳转备份失败:', error)
  }
}

/**
 * 测试备份功能（仅开发环境使用）
 */
export function testBackupFunction(): void {
  if (import.meta.env.DEV) {
    console.log('=== SessionStorage 备份测试 ===')
    
    // 添加一些测试数据
    sessionStorage.setItem('test_data', 'should_be_excluded')
    sessionStorage.setItem('temp_cache', 'should_be_excluded')
    sessionStorage.setItem('normal_data', 'should_be_included')
    sessionStorage.setItem('tianji_chat_traceNo', 'should_be_included')
    
    // 执行备份
    backupSessionStorage()
    
    // 显示备份信息
    const info = getBackupInfo()
    console.log('备份信息:', info)
    
    // 清理测试数据
    sessionStorage.removeItem('test_data')
    sessionStorage.removeItem('temp_cache')
    sessionStorage.removeItem('normal_data')
  }
}

/**
 * 支付返回专用：检查并恢复支付前的数据
 */
export function restoreFromPayment(): boolean {
  try {
    const paymentContextStr = localStorage.getItem('tianji_payment_context')
    
    if (!paymentContextStr) {
      return false
    }
    
    const paymentContext = JSON.parse(paymentContextStr)
    
    // 检查是否在合理时间内（1小时）
    const now = Date.now()
    const timeDiff = now - paymentContext.timestamp
    if (timeDiff > 60 * 60 * 1000) { // 1小时
      localStorage.removeItem('tianji_payment_context')
      return false
    }
    
    // 恢复 sessionStorage
    const restored = restoreSessionStorage()
    
    // 设置支付恢复完成标记（5秒后自动清除）
    sessionStorage.setItem('tianji_payment_restored', 'true')
    setTimeout(() => {
      sessionStorage.removeItem('tianji_payment_restored')
    }, 5000)
    
    // 清除支付上下文
    localStorage.removeItem('tianji_payment_context')
    
    console.log('支付返回恢复完成')
    return restored
  } catch (error) {
    console.error('支付返回恢复失败:', error)
    localStorage.removeItem('tianji_payment_context')
    return false
  }
} 